import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Prior Lucan (docs/ANTIHEROES.md): he prayed in the chapel to the end, and nobody came. The titan dreams him as a
 * tall prior in a robe of black glass to the ground, a deep peaked hood with two violet eyes in the dark of it, and
 * behind his head a broken halo of glass cracked through with violet light. In his left hand a crozier topped with
 * the chapel's broken sun; from his right a censer on a long chain (his whip), smoking violet. A mantle of smoke
 * hangs off his shoulders.
 *
 * Built on Ser Corvin's bones (the fallen sentinel's) so anim/creatures/lucan.ts reuses their walk, reel and fall:
 * body, chest, head, flapF/flapB (the robe's front and back), armL/foreL/shield (the crozier), armR/foreR/sword
 * (the censer's chain), legs (thigh, shin). Faces +Z, `R` limbs on +X.
 */

/** Model height (the halo's top). */
export const LUCAN_HEIGHT = 2.35;

export const lucan = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 919);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#2b2834', { roughness: 0.16, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#18151f', { roughness: 0.28, metalness: 0.5, bumpScale: 0.02 });
  const robe = cloth('#1d1626', { side: THREE.DoubleSide });
  const stole = cloth('#2c1d3d', { side: THREE.DoubleSide });
  const iron = metal('#3b3a44', { roughness: 0.55, metalness: 0.6, bumpScale: 0.04 });
  const wood = std('#1e1712', { roughness: 0.9, bumpScale: 0.06 });
  const voidM = std('#050308', { roughness: 1 });
  const vio = emis('#efe2ff', '#9a5cff', 2.8);
  const vioF = emis('#e6d6ff', '#b07aff', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#1a1422', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: glass greaves under the robe, pointed sabatons ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.14, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.14, 1.0, 0), V(k * 0.15, 0.56, 0.02), 0.12, 0.095, robe, 7);
    const shinB = bone(hip, V(k * 0.15, 0.55, 0.02), `shin${s}`);
    const lo = shinB.abs;
    // The robe's hem over each shin (it swings with the step), the greave showing under it.
    const hem = M(new THREE.CylinderGeometry(0.13, 0.19, 0.42, 8, 1, true), robe, lo);
    hem.position.set(k * 0.15, 0.36, 0.02);
    limb(lo, V(k * 0.15, 0.3, 0.02), V(k * 0.15, 0.12, 0.0), 0.08, 0.07, glass, 6);
    const sab = M(new THREE.BoxGeometry(0.15, 0.09, 0.25), glassD, lo);
    sab.position.set(k * 0.15, 0.05, 0.05);
    const toe = M(new THREE.ConeGeometry(0.065, 0.16, 4), glassD, lo);
    toe.position.set(k * 0.15, 0.04, 0.22);
    toe.rotation.set(Math.PI / 2, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.5);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: the robe's skirt, a rope belt with a hanging rosary of glass beads, the robe's front and back ---------- */
  const bodyB = bone(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.26, 0.36, 0.36, 10, 1, true), robe, body).position.set(0, 0.9, 0);
  const belt = M(new THREE.TorusGeometry(0.265, 0.03, 4, 12), stole, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.1, 0);
  belt.scale.set(1, 0.85, 1);
  // The rosary: beads down the left hip, the last a cracked glass bead lit violet.
  for (let i = 0; i < 7; i++) M(new THREE.SphereGeometry(0.022, 5, 4), glassD, body).position.set(-0.2 + Math.sin(i * 0.5) * 0.02, 1.06 - i * 0.055, 0.17 - i * 0.005);
  M(new THREE.OctahedronGeometry(0.045, 0), vio, body).position.set(-0.2, 0.66, 0.16);
  const flapF = bone(body, V(0, 1.08, 0.2), 'flapF');
  k4.panel(flapF.abs, V(0, 1.09, 0.24), 0.36, 1.0, 0.03, 1, robe, 0.3);
  // The stole down the front, a crack of violet down its middle.
  k4.panel(flapF.abs, V(0, 1.1, 0.26), 0.14, 0.92, 0.0, 1, stole, 0.15);
  k4.crack(flapF.abs, [V(0, 1.02, 0.275), V(0.01, 0.8, 0.275), V(-0.01, 0.55, 0.27), V(0.0, 0.3, 0.26)], 0.008, vio, 0.03);
  const flapB = bone(body, V(0, 1.08, -0.2), 'flapB');
  k4.panel(flapB.abs, V(0, 1.09, -0.24), 0.42, 1.04, 0.03, -1, robe, 0.3);

  /* ---------- chest: a glass cuirass under a glass mantelet; the crack of the chapel's fall across it ---------- */
  const chestB = bone(body, V(0, 1.14, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.4, 0.0, 0.28, 0.3, 0.2, 9, 6);
  k4.swell(chest, robe, 0, 1.2, 0.0, 0.27, 0.12, 0.2, 8, 4);
  // A short mantelet of glass over the shoulders (a cleric's cape, not a knight's pauldrons).
  const capelet = M(new THREE.CylinderGeometry(0.2, 0.36, 0.2, 10, 1, true), glassD, chest);
  capelet.position.set(0, 1.58, -0.01);
  capelet.scale.set(1, 1, 0.82);
  const capeD = M(new THREE.CylinderGeometry(0.36, 0.37, 0.035, 10, 1, true), glass, chest);
  capeD.position.set(0, 1.47, -0.01);
  capeD.scale.set(1, 1, 0.82);
  k4.crack(chest, [V(0.22, 1.58, 0.16), V(0.12, 1.5, 0.21), V(0.04, 1.42, 0.22), V(-0.06, 1.33, 0.21), V(-0.02, 1.24, 0.2)], 0.012, vio, 0.05);
  k4.crack(capelet, [V(-0.18, 0.1, 0.2), V(-0.26, -0.02, 0.26), V(-0.33, -0.12, 0.28)], 0.008, vio, 0.03);
  // The sun of the chapel on his breast, its rays broken.
  const sun = G(chest, 0, 1.4, 0.2);
  M(new THREE.TorusGeometry(0.07, 0.012, 4, 12, Math.PI * 1.55), iron, sun).rotation.z = 0.6;
  M(new THREE.SphereGeometry(0.03, 6, 4), vio, sun);
  glowSprite(chest, '#b07aff', 0.4, V(0, 1.4, 0.26), 0.5);

  /* ---------- the mantle of smoke down his back ---------- */
  const mantle = G(chest, 0, 1.62, -0.2);
  for (const [x, w, h, rz] of [[-0.17, 0.3, 1.3, 0.05], [0.17, 0.3, 1.25, -0.05], [0, 0.36, 1.45, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.1, 0, rz);
  }

  /* ---------- head: a deep peaked hood, the dark inside it, two violet eyes; a broken halo behind ---------- */
  const headB = bone(chest, V(0, 1.66, 0.01), 'head');
  const head = headB.abs;
  const hood = M(new THREE.ConeGeometry(0.2, 0.52, 9, 1, true), glass, head);
  hood.position.set(0, 1.93, -0.02);
  hood.rotation.x = -0.18;
  const hoodBack = M(new THREE.SphereGeometry(0.19, 9, 6), glass, head);
  hoodBack.position.set(0, 1.82, -0.04);
  hoodBack.scale.set(1, 1.05, 1);
  // The opening: a dark oval, with the lip of the hood around it.
  const face = M(new THREE.SphereGeometry(0.13, 8, 6), voidM, head);
  face.position.set(0, 1.8, 0.09);
  face.scale.set(0.9, 1.15, 0.6);
  const lip = M(new THREE.TorusGeometry(0.13, 0.025, 4, 12), glassD, head);
  lip.position.set(0, 1.81, 0.14);
  lip.scale.set(0.92, 1.25, 1);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.022, 6, 4), vio, head).position.set(k * 0.045, 1.83, 0.17);
    glowSprite(head, '#b07aff', 0.22, V(k * 0.045, 1.83, 0.2), 0.95);
  }
  k4.crack(hood, [V(0.08, 0.15, 0.1), V(0.05, 0.02, 0.15), V(0.09, -0.12, 0.17)], 0.007, vio, 0.03);
  // The halo: a ring of black glass standing behind the head, broken through on one side, light in its cracks.
  const halo = G(head, 0, 1.98, -0.24);
  halo.rotation.x = 0.12;
  M(new THREE.TorusGeometry(0.3, 0.028, 5, 24, Math.PI * 1.7), glass, halo).rotation.z = -0.55;
  M(new THREE.TorusGeometry(0.3, 0.008, 3, 24, Math.PI * 1.7), vio, halo).rotation.z = -0.55;
  for (const a of [0.4, 1.4, 2.6, 3.6, 4.6]) {
    const sp = M(new THREE.ConeGeometry(0.022, 0.12, 4), glass, halo);
    sp.position.set(Math.cos(a) * 0.36, Math.sin(a) * 0.36, 0);
    sp.rotation.z = a - Math.PI / 2;
  }
  // The shard that broke off, hanging in the air beside the gap.
  const loose = M(new THREE.OctahedronGeometry(0.05, 0), vio, halo);
  loose.position.set(0.38, -0.12, 0.02);
  loose.scale.set(0.6, 1.4, 0.6);
  glowSprite(halo, '#b07aff', 0.85, V(0, 0, -0.02), 0.45);

  /* ---------- the crozier hand: a staff of charred wood bound in iron, the chapel's sun broken at its head ---------- */
  const armLB = bone(chest, V(-0.33, 1.56, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.34, 1.56, 0), V(-0.4, 1.24, 0.05), 0.09, 0.08, robe, 6);
  const foreLB = bone(aL, V(-0.4, 1.23, 0.06), 'foreL');
  const fL = foreLB.abs;
  limb(fL, V(-0.4, 1.23, 0.06), V(-0.47, 1.12, 0.22), 0.085, 0.06, robe, 6);
  M(new THREE.SphereGeometry(0.05, 6, 4), glassD, fL).position.set(-0.47, 1.12, 0.24);
  const staffB = bone(fL, V(-0.47, 1.12, 0.25), 'shield');
  const sf = staffB.abs;
  {
    const st = G(sf, -0.47, 1.12, 0.25);
    M(new THREE.CylinderGeometry(0.026, 0.032, 2.3, 6), wood, st).position.y = 0.05;
    for (const y of [-0.9, -0.3, 0.35, 0.95]) M(new THREE.CylinderGeometry(0.036, 0.036, 0.05, 6), iron, st).position.y = y;
    // The broken sun: an iron ring open at the top, its rays snapped, a violet ember hanging in the middle.
    const top = G(st, 0, 1.36, 0);
    M(new THREE.TorusGeometry(0.17, 0.022, 4, 16, Math.PI * 1.6), iron, top).rotation.z = -Math.PI * 0.3 + Math.PI;
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + 0.3;
      const len = i % 3 === 0 ? 0.06 : 0.13;
      const ray = M(new THREE.BoxGeometry(0.018, len, 0.018), iron, top);
      ray.position.set(Math.cos(a) * (0.19 + len / 2), Math.sin(a) * (0.19 + len / 2), 0);
      ray.rotation.z = a - Math.PI / 2;
    }
    const core = M(new THREE.OctahedronGeometry(0.06, 0), vio, top);
    core.scale.set(0.8, 1.2, 0.8);
    glowSprite(top, '#b07aff', 0.6, V(0, 0, 0.02), 0.6);
  }

  /* ---------- the censer hand: a long chain from the fist to a censer of black glass, violet smoke out of its slits ---------- */
  const armRB = bone(chest, V(0.33, 1.56, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.34, 1.56, 0), V(0.42, 1.24, 0.02), 0.09, 0.08, robe, 6);
  const foreRB = bone(aR, V(0.42, 1.23, 0.02), 'foreR');
  const fR = foreRB.abs;
  limb(fR, V(0.42, 1.23, 0.02), V(0.45, 0.96, 0.1), 0.085, 0.06, robe, 6);
  M(new THREE.SphereGeometry(0.05, 6, 4), glassD, fR).position.set(0.45, 0.94, 0.11);
  const censerB = bone(fR, V(0.45, 0.92, 0.11), 'sword');
  const cs = censerB.abs;
  const flames: THREE.Mesh[] = [];
  {
    const ch = G(cs, 0.45, 0.92, 0.11);
    // Links down from the fist.
    for (let i = 0; i < 6; i++) {
      const l = M(new THREE.TorusGeometry(0.022, 0.006, 3, 6), iron, ch);
      l.position.set(0, -0.04 - i * 0.05, 0);
      l.rotation.y = i % 2 ? Math.PI / 2 : 0;
    }
    const cn = G(ch, 0, -0.42, 0);
    M(new THREE.ConeGeometry(0.07, 0.1, 8), glassD, cn).position.y = 0.09;
    const bowl = M(new THREE.SphereGeometry(0.09, 8, 6), glass, cn);
    bowl.scale.set(1, 0.9, 1);
    M(new THREE.TorusGeometry(0.092, 0.012, 3, 10), iron, cn).rotation.x = Math.PI / 2;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      const slit = M(new THREE.BoxGeometry(0.018, 0.06, 0.01), vio, cn);
      slit.position.set(Math.sin(a) * 0.088, -0.01, Math.cos(a) * 0.088);
      slit.rotation.y = a;
    }
    M(new THREE.ConeGeometry(0.03, 0.05, 6), iron, cn).position.y = -0.1;
    for (const [x, z, sc, h] of [[0, 0, 1, 0.16], [0.03, -0.02, 0.7, 0.12]] as const) {
      const f = M(new THREE.ConeGeometry(0.035 * sc, h, 6), vioF, cn);
      f.position.set(x, 0.14 + h / 2, z);
      flames.push(f);
    }
    glowSprite(cn, '#b07aff', 0.5, V(0, 0, 0.02), 0.6);
  }

  /* ---------- smoke breathing off him ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.3, -0.2, 0.7, 1.5], [-0.28, -0.22, 0.7, 1.45], [0.04, -0.32, 0.9, 1.25], [-0.1, -0.3, 0.6, 0.8]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    flames.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
    loose.rotation.y = n * 0.4;
  };
  t(0);
  return done(c, LUCAN_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: staffB.p, armR: armRB.p, foreR: foreRB.p, sword: censerB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
